import { useLocalization } from 'localization'
import { message } from 'antd'
import { useMutation, useQueryClient } from 'react-query'
import { requestReport, startAnalyze } from './services'
import queries from 'api/queries'
import { IAnalyzeStatus, IReport } from './models'
import { ReportStatus, TaxAnalysisStatus } from './dtos'
import { ResponseErrorType } from 'types/custom'

export const useStartAnalyze = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(startAnalyze, {
		onMutate: () => {
			queryClient.setQueryData<IAnalyzeStatus>([queries.analyzeStatus], prev => ({
				...prev,
				progress: prev?.progress || 0,
				status: TaxAnalysisStatus.IN_PROGRESS,
			}))
		},
		onSuccess: () => {
			message.success(t('reports.analyze.success'))
			queryClient.invalidateQueries([queries.analyzeStatus])
			queryClient.invalidateQueries([queries.analyzeResults])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			queryClient.setQueryData<IAnalyzeStatus>([queries.analyzeStatus], prev => ({
				...prev!,
				status: TaxAnalysisStatus.EMPTY,
			}))

			if (error?.response?.status !== 401) {
				message.error(t('reports.analyze.error'))
			}
		},
	})
}

export const useRequestReport = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(requestReport, {
		onMutate: ({ slug, taxYear }) => {
			queryClient.setQueryData<IReport[]>([queries.reports, taxYear], prev =>
				prev!.map(item => ({
					...item,
					status: item.name === slug ? ReportStatus.IN_PROGRESS : item.status,
				}))
			)
		},
		onSuccess: (_, { slug }) => {
			message.success(t('reports.request.success', { report: slug }))
			queryClient.invalidateQueries([queries.reports])
			queryClient.invalidateQueries([queries.reportBySlug])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType, { slug, taxYear }) => {
			queryClient.setQueryData<IReport[]>([queries.reports, taxYear], prev =>
				prev!.map(item => ({
					...item,
					status: item.name === slug ? ReportStatus.ERROR : item.status,
				}))
			)
			if (error?.response?.status !== 401) {
				message.error(t('reports.request.error', { report: slug }))
			}
		},
	})
}
